import ScreenWrapper from '@components/layout/screen-wrapper';
import {
  BrandLogo,
  Button,
  Field,
  Input,
  Stack,
  Text,
} from '@components/ui';
import {
  type ForgotPasswordFormValues,
  forgotPasswordSchema,
} from '@features/auth/schemas/forgotPasswordSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiErrorMessage } from '@lib/api/apiError';
import { useResetPasswordMutation } from '@lib/api/auth';
import { persistUser, setLoggedIn } from '@lib/auth/session';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import type { AuthLocationState } from '@typings/auth';
import { useEffect, useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AuthSplitCard } from './auth-split-card';


const authPageSx = {
  height: '100dvh',
  maxHeight: '100dvh',
  width: '100%',
  overflow: 'auto',
};

const authContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  height: 'auto',
  width: '100%',
};

interface PasswordFieldProps {
  label: string;
  placeholder: string;
  invalid: boolean;
  error?: string;
  visible: boolean;
  toggleLabel: string;
  onToggleVisibility: () => void;
  registration: UseFormRegisterReturn;
}

const PasswordField = ({
  label,
  placeholder,
  invalid,
  error,
  visible,
  toggleLabel,
  onToggleVisibility,
  registration,
}: PasswordFieldProps): JSX.Element => (
  <Field sx={{ mb: 2.5 }} label={label} error={invalid ? error : undefined}>
    <Input
      type={visible ? 'text' : 'password'}
      invalid={invalid}
      placeholder={placeholder}
      autoComplete="new-password"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              type="button"
              edge="end"
              aria-label={toggleLabel}
              onClick={onToggleVisibility}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {visible ? (
                <Visibility sx={{ fontSize: 20 }} />
              ) : (
                <VisibilityOff sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...registration}
    />
  </Field>
);

const ForgetPassword = (): JSX.Element | null => {
  const { t } = useTranslation();
  const ebayUrl = new URL(window.location.href).searchParams;
  const itemId = ebayUrl.get('itemID');
  const navigator = useNavigate();
  const location = useLocation();
  const resetPassword = useResetPasswordMutation();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted, isValid },
  } = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
  });

  const locationState = location?.state as AuthLocationState | undefined;

  useEffect(() => {
    if (!locationState?.phoneNumber) {
      navigator('/get-otp', { replace: true });
    }
  }, [locationState?.phoneNumber, navigator]);

  if (!locationState?.phoneNumber) {
    return null;
  }

  const phoneNumber = locationState.phoneNumber;

  const showNewPasswordError =
    (isSubmitted || errors.newPassword) && errors.newPassword;
  const showConfirmPasswordError =
    (isSubmitted || errors.confirmPassword) && errors.confirmPassword;

  const onSubmit = (values: ForgotPasswordFormValues): void => {
    resetPassword
      .mutateAsync({
        phoneNumber,
        password: values.newPassword,
        confirmPassword: values.confirmPassword,
        parentUserId: null,
      })
      .then((res) => {
        toast.success(
          'Password reset successfully. Sign in to continue.'
        );
        persistUser(res.data.data);
        localStorage.setItem('userRole', res.data.data.userRole);
        setLoggedIn(true);
        if (itemId) {
          navigator(`/ebay-hire-a-r8tr?itemID=${itemId}`);
        } else {
          if (locationState?.title === 'Reset Password') {
            navigator('/login');
          } else {
            if (res.data.data.userRole === 'buyer') {
              navigator('/');
            } else {
              navigator('/personal-info');
            }
          }
        }
      })
      .catch((error: unknown) => {
        toast.error(
          apiErrorMessage(error) ?? 'Something went wrong'
        );
      });
  };

  return (
    <ScreenWrapper pageSx={authPageSx} containerSx={authContainerSx}>
      <Box
        sx={{
          m: 'auto',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <AuthSplitCard>
          <BrandLogo
            alt={t('common.logoAlt')}
            height={48}
            sx={{ mx: 'auto', mb: 1.5 }}
          />
          <Text
            variant="subtitle"
            align="center"
            sx={{ mb: 1, fontWeight: 600, display: { md: 'none' } }}
          >
            {t('auth.tagline')}
          </Text>
          <Text
            variant="body"
            tone="muted"
            align="center"
            sx={{ mb: 3.5, fontSize: '0.875rem' }}
          >
            {t(locationState?.title ?? 'auth.setPassword')}
          </Text>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
          >
            <PasswordField
              label={t('auth.newPassword')}
              placeholder={t('auth.newPasswordPlaceholder')}
              invalid={Boolean(showNewPasswordError)}
              error={t(errors.newPassword?.message ?? '')}
              visible={showNewPassword}
              toggleLabel={t('common.togglePassword')}
              onToggleVisibility={() => setShowNewPassword(!showNewPassword)}
              registration={register('newPassword')}
            />
            <PasswordField
              label={t('auth.confirmPassword')}
              placeholder={t('auth.confirmPasswordPlaceholder')}
              invalid={Boolean(showConfirmPasswordError)}
              error={t(errors.confirmPassword?.message ?? '')}
              visible={showConfirmPassword}
              toggleLabel={t('common.togglePassword')}
              onToggleVisibility={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
              registration={register('confirmPassword')}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={!isValid}
              loading={resetPassword.isPending}
              sx={{ mt: 1 }}
            >
              {t('common.submit')}
            </Button>

            <Stack
              align="stretch"
              gap="md"
              sx={{ mt: 'auto', pt: 5, width: '100%', textAlign: 'center' }}
            >
              <Text
                variant="body"
                tone="muted"
                sx={{
                  fontSize: '0.875rem',
                  '& strong': { color: 'text.primary' },
                }}
              >
                <Trans
                  i18nKey="auth.rememberPrompt"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() =>
                  navigator(itemId ? `/login?itemID=${itemId}` : '/login')
                }
              >
                {t('common.signIn')}
              </Button>
            </Stack>
          </Box>
        </AuthSplitCard>
      </Box>
    </ScreenWrapper>
  );
};
export default ForgetPassword;
